import React, { useState, useMemo, useCallback, useRef } from 'react';
import { TEMPLATE_LIST, TEMPLATE_CATEGORIES, PRESET_TEMPLATES, TEMPLATES } from '../../templates';
import { scrollEditorToSection } from '../../utils/scrollEditor';

/** Default pinned template IDs — first 3 presets */
const DEFAULT_PINNED_IDS = PRESET_TEMPLATES.slice(0, 3).map(t => t.id);

/**
 * SVG wireframe thumbnails for each template.
 * Simple geometric outlines that convey the layout at a glance.
 * Viewbox is 80x100 (4:5 aspect ratio matching slides).
 */
const TEMPLATE_THUMBNAILS = {
  'forest-classic': (
    <svg viewBox="0 0 80 100" className="w-full h-full">
      <circle cx="12" cy="10" r="4" fill="currentColor" opacity="0.5" />
      <rect x="19" y="7" width="22" height="3" rx="1" fill="currentColor" opacity="0.3" />
      <rect x="19" y="12" width="22" height="2" rx="1" fill="currentColor" opacity="0.15" />
      <rect x="5" y="48" width="70" height="47" rx="5" fill="currentColor" opacity="0.08" stroke="currentColor" strokeOpacity="0.15" strokeWidth="0.5" />
      <rect x="10" y="54" width="30" height="3" rx="1" fill="currentColor" opacity="0.4" />
      <rect x="10" y="60" width="55" height="8" rx="1" fill="currentColor" opacity="0.6" />
      <rect x="10" y="72" width="50" height="3" rx="1" fill="currentColor" opacity="0.25" />
      <rect x="10" y="78" width="45" height="3" rx="1" fill="currentColor" opacity="0.25" />
      <rect x="10" y="87" width="20" height="2" rx="1" fill="currentColor" opacity="0.15" />
      <rect x="48" y="85" width="18" height="6" rx="3" fill="currentColor" opacity="0.3" />
    </svg>
  ),
  'bold-magazine': (
    <svg viewBox="0 0 80 100" className="w-full h-full">
      <rect x="12" y="30" width="56" height="10" rx="1" fill="currentColor" opacity="0.6" />
      <rect x="18" y="43" width="44" height="5" rx="1" fill="currentColor" opacity="0.3" />
      <rect x="5" y="54" width="70" height="0.8" fill="currentColor" opacity="0.2" />
      <rect x="10" y="60" width="55" height="3" rx="1" fill="currentColor" opacity="0.2" />
      <rect x="10" y="66" width="48" height="3" rx="1" fill="currentColor" opacity="0.2" />
      <circle cx="12" cy="92" r="4" fill="currentColor" opacity="0.3" />
      <rect x="48" y="90" width="24" height="2.5" rx="1" fill="currentColor" opacity="0.15" />
    </svg>
  ),
  'clean-minimal': (
    <svg viewBox="0 0 80 100" className="w-full h-full">
      <circle cx="12" cy="10" r="3.5" fill="currentColor" opacity="0.35" />
      <rect x="8" y="40" width="50" height="8" rx="1" fill="currentColor" opacity="0.55" />
      <rect x="8" y="52" width="16" height="2.5" rx="1" fill="#34d399" opacity="0.6" />
      <rect x="8" y="59" width="55" height="3" rx="1" fill="currentColor" opacity="0.2" />
      <rect x="8" y="65" width="48" height="3" rx="1" fill="currentColor" opacity="0.2" />
      <rect x="8" y="71" width="40" height="3" rx="1" fill="currentColor" opacity="0.2" />
      <rect x="8" y="90" width="25" height="2.5" rx="1" fill="currentColor" opacity="0.15" />
    </svg>
  ),
  'top-heavy': (
    <svg viewBox="0 0 80 100" className="w-full h-full">
      <rect x="5" y="5" width="70" height="47" rx="5" fill="currentColor" opacity="0.08" stroke="currentColor" strokeOpacity="0.15" strokeWidth="0.5" />
      <rect x="10" y="12" width="30" height="3" rx="1" fill="currentColor" opacity="0.4" />
      <rect x="10" y="18" width="55" height="8" rx="1" fill="currentColor" opacity="0.6" />
      <rect x="10" y="30" width="50" height="3" rx="1" fill="currentColor" opacity="0.25" />
      <rect x="10" y="36" width="45" height="3" rx="1" fill="currentColor" opacity="0.25" />
      <rect x="10" y="43" width="20" height="2" rx="1" fill="currentColor" opacity="0.15" />
      <circle cx="12" cy="88" r="4" fill="currentColor" opacity="0.4" />
      <rect x="19" y="86" width="22" height="3" rx="1" fill="currentColor" opacity="0.25" />
      <rect x="55" y="86" width="18" height="3" rx="1" fill="currentColor" opacity="0.15" />
    </svg>
  ),
  'noir-elegance': (
    <svg viewBox="0 0 80 100" className="w-full h-full">
      <rect x="20" y="28" width="12" height="0.5" fill="currentColor" opacity="0.3" />
      <rect x="37" y="26" width="4" height="4" rx="0" fill="none" stroke="currentColor" strokeOpacity="0.3" strokeWidth="0.5" transform="rotate(45 39 28)" />
      <rect x="47" y="28" width="12" height="0.5" fill="currentColor" opacity="0.3" />
      <rect x="14" y="36" width="52" height="9" rx="1" fill="currentColor" opacity="0.55" />
      <rect x="20" y="48" width="40" height="4" rx="1" fill="currentColor" opacity="0.25" />
      <rect x="15" y="57" width="50" height="3" rx="1" fill="currentColor" opacity="0.2" />
      <rect x="18" y="63" width="44" height="3" rx="1" fill="currentColor" opacity="0.2" />
      <rect x="20" y="72" width="12" height="0.5" fill="currentColor" opacity="0.3" />
      <rect x="37" y="70" width="4" height="4" rx="0" fill="none" stroke="currentColor" strokeOpacity="0.3" strokeWidth="0.5" transform="rotate(45 39 72)" />
      <rect x="47" y="72" width="12" height="0.5" fill="currentColor" opacity="0.3" />
      <rect x="22" y="90" width="36" height="2" rx="1" fill="currentColor" opacity="0.12" />
    </svg>
  ),
  'brutalist': (
    <svg viewBox="0 0 80 100" className="w-full h-full">
      <rect x="5" y="5" width="18" height="2.5" rx="0" fill="currentColor" opacity="0.2" />
      <path d="M68 5 L75 5 L75 12" fill="none" stroke="currentColor" strokeOpacity="0.2" strokeWidth="1" />
      <rect x="5" y="35" width="45" height="8" rx="0" fill="currentColor" opacity="0.55" />
      <rect x="5" y="47" width="60" height="3" rx="0" fill="currentColor" opacity="0.25" />
      <rect x="5" y="53" width="52" height="3" rx="0" fill="currentColor" opacity="0.25" />
      <rect x="5" y="59" width="55" height="3" rx="0" fill="currentColor" opacity="0.25" />
      <path d="M5 95 L5 88 L12 88" fill="none" stroke="currentColor" strokeOpacity="0.2" strokeWidth="1" />
      <rect x="50" y="92" width="25" height="2.5" rx="0" fill="currentColor" opacity="0.15" />
    </svg>
  ),
  'split': (
    <svg viewBox="0 0 80 100" className="w-full h-full">
      <rect x="0" y="0" width="44" height="100" fill="currentColor" opacity="0.08" />
      <circle cx="12" cy="12" r="3.5" fill="currentColor" opacity="0.35" />
      <rect x="7" y="38" width="30" height="6" rx="1" fill="currentColor" opacity="0.55" />
      <rect x="7" y="48" width="28" height="3" rx="1" fill="currentColor" opacity="0.25" />
      <rect x="7" y="54" width="25" height="3" rx="1" fill="currentColor" opacity="0.25" />
      <rect x="7" y="60" width="30" height="3" rx="1" fill="currentColor" opacity="0.25" />
      <rect x="7" y="88" width="22" height="2.5" rx="1" fill="currentColor" opacity="0.15" />
      <rect x="44" y="0" width="0.5" height="100" fill="currentColor" opacity="0.06" />
    </svg>
  ),
  'full-bleed': (
    <svg viewBox="0 0 80 100" className="w-full h-full">
      <rect x="5" y="35" width="70" height="12" rx="1" fill="currentColor" opacity="0.55" />
      <rect x="5" y="50" width="60" height="12" rx="1" fill="currentColor" opacity="0.55" />
      <rect x="5" y="65" width="45" height="12" rx="1" fill="currentColor" opacity="0.55" />
      <rect x="5" y="82" width="70" height="0.8" fill="currentColor" opacity="0.15" />
      <rect x="5" y="87" width="40" height="3" rx="1" fill="currentColor" opacity="0.2" />
      <rect x="55" y="87" width="20" height="3" rx="1" fill="currentColor" opacity="0.15" />
    </svg>
  ),
  'gradient-card': (
    <svg viewBox="0 0 80 100" className="w-full h-full">
      <circle cx="40" cy="14" r="4" fill="currentColor" opacity="0.4" />
      <rect x="10" y="24" width="60" height="68" rx="6" fill="none" stroke="url(#gc-grad)" strokeWidth="1.5" opacity="0.5" />
      <rect x="12" y="26" width="56" height="64" rx="5" fill="currentColor" opacity="0.06" />
      <rect x="17" y="34" width="25" height="3" rx="1" fill="currentColor" opacity="0.35" />
      <rect x="17" y="41" width="46" height="7" rx="1" fill="currentColor" opacity="0.55" />
      <rect x="17" y="53" width="42" height="3" rx="1" fill="currentColor" opacity="0.2" />
      <rect x="17" y="59" width="38" height="3" rx="1" fill="currentColor" opacity="0.2" />
      <rect x="17" y="65" width="30" height="3" rx="1" fill="currentColor" opacity="0.2" />
      <rect x="17" y="78" width="18" height="2" rx="1" fill="currentColor" opacity="0.12" />
      <defs>
        <linearGradient id="gc-grad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#34d399" />
          <stop offset="50%" stopColor="#3b82f6" />
          <stop offset="100%" stopColor="#a855f7" />
        </linearGradient>
      </defs>
    </svg>
  ),
  'sidebar-accent': (
    <svg viewBox="0 0 80 100" className="w-full h-full">
      <rect x="3" y="0" width="3" height="100" rx="1" fill="url(#sa-grad)" opacity="0.6" />
      <circle cx="18" cy="14" r="3.5" fill="currentColor" opacity="0.35" />
      <rect x="12" y="38" width="48" height="7" rx="1" fill="currentColor" opacity="0.55" />
      <rect x="12" y="50" width="55" height="3" rx="1" fill="currentColor" opacity="0.2" />
      <rect x="12" y="56" width="48" height="3" rx="1" fill="currentColor" opacity="0.2" />
      <rect x="12" y="62" width="42" height="3" rx="1" fill="currentColor" opacity="0.2" />
      <rect x="12" y="90" width="22" height="2.5" rx="1" fill="currentColor" opacity="0.15" />
      <defs>
        <linearGradient id="sa-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#34d399" />
          <stop offset="100%" stopColor="#3b82f6" />
        </linearGradient>
      </defs>
    </svg>
  ),
  'centered-stack': (
    <svg viewBox="0 0 80 100" className="w-full h-full">
      <circle cx="40" cy="18" r="4" fill="currentColor" opacity="0.35" />
      <rect x="14" y="40" width="52" height="8" rx="1" fill="currentColor" opacity="0.55" />
      <rect x="18" y="54" width="44" height="3" rx="1" fill="currentColor" opacity="0.2" />
      <rect x="22" y="60" width="36" height="3" rx="1" fill="currentColor" opacity="0.2" />
      <rect x="20" y="66" width="40" height="3" rx="1" fill="currentColor" opacity="0.2" />
      <rect x="26" y="88" width="28" height="2.5" rx="1" fill="currentColor" opacity="0.12" />
    </svg>
  ),
  'dynamic-diagonal': (
    <svg viewBox="0 0 80 100" className="w-full h-full">
      <circle cx="12" cy="10" r="3.5" fill="currentColor" opacity="0.35" />
      <polygon points="0,35 80,20 80,100 0,100" fill="currentColor" opacity="0.07" />
      <line x1="0" y1="35" x2="80" y2="20" stroke="#34d399" strokeWidth="1" opacity="0.35" />
      <rect x="8" y="52" width="45" height="7" rx="1" fill="currentColor" opacity="0.55" />
      <rect x="8" y="63" width="55" height="3" rx="1" fill="currentColor" opacity="0.25" />
      <rect x="8" y="69" width="48" height="3" rx="1" fill="currentColor" opacity="0.25" />
      <rect x="8" y="75" width="40" height="3" rx="1" fill="currentColor" opacity="0.25" />
      <rect x="8" y="90" width="20" height="2" rx="1" fill="currentColor" opacity="0.12" />
      <rect x="52" y="90" width="20" height="2" rx="1" fill="currentColor" opacity="0.12" />
    </svg>
  ),
  'corner-frame': (
    <svg viewBox="0 0 80 100" className="w-full h-full">
      <path d="M8 28 L8 8 L28 8" fill="none" stroke="currentColor" strokeOpacity="0.3" strokeWidth="1.2" />
      <circle cx="8" cy="8" r="2" fill="currentColor" opacity="0.3" />
      <circle cx="18" cy="20" r="3.5" fill="currentColor" opacity="0.3" />
      <path d="M72 72 L72 92 L52 92" fill="none" stroke="currentColor" strokeOpacity="0.15" strokeWidth="0.8" />
      <rect x="10" y="50" width="50" height="7" rx="1" fill="currentColor" opacity="0.55" />
      <rect x="10" y="61" width="58" height="3" rx="1" fill="currentColor" opacity="0.2" />
      <rect x="10" y="67" width="52" height="3" rx="1" fill="currentColor" opacity="0.2" />
      <rect x="10" y="73" width="45" height="3" rx="1" fill="currentColor" opacity="0.2" />
      <rect x="10" y="88" width="20" height="2" rx="1" fill="currentColor" opacity="0.12" />
      <rect x="50" y="86" width="18" height="6" rx="3" fill="currentColor" opacity="0.2" />
    </svg>
  ),
  'ribbon-band': (
    <svg viewBox="0 0 80 100" className="w-full h-full">
      <circle cx="12" cy="12" r="3.5" fill="currentColor" opacity="0.35" />
      <rect x="0" y="40" width="80" height="52" fill="currentColor" opacity="0.07" />
      <line x1="8" y1="40" x2="72" y2="40" stroke="#34d399" strokeWidth="1" opacity="0.3" />
      <rect x="8" y="48" width="45" height="7" rx="1" fill="currentColor" opacity="0.55" />
      <rect x="8" y="60" width="55" height="3" rx="1" fill="currentColor" opacity="0.22" />
      <rect x="8" y="66" width="48" height="3" rx="1" fill="currentColor" opacity="0.22" />
      <rect x="8" y="72" width="40" height="3" rx="1" fill="currentColor" opacity="0.22" />
      <rect x="8" y="84" width="18" height="2" rx="1" fill="currentColor" opacity="0.12" />
      <rect x="52" y="82" width="18" height="6" rx="3" fill="currentColor" opacity="0.2" />
    </svg>
  ),
};

/**
 * Instrument-style template card with LED dot and SVG thumbnail.
 * 
 * Modes:
 * - Pinned zone: shows × on hover (onUnpin)
 * - Library zone: shows pin icon on hover (onPin), filled if isPinned
 * 
 * NOTE: Uses template literals NOT cn() — tailwind-merge strips shared-prefix custom classes.
 */
function TemplateCard({ template, isActive, onClick, onPin = null, onUnpin = null, isPinned = false, pinsFull = false }) {
  const accent = template.accent || '#34d399';
  const accentGlow = template.accentGlow || 'rgba(52, 211, 153, 0.25)';

  return (
    <button
      onClick={onClick}
      className={`tpl-btn${isActive ? ' tpl-btn-active' : ''} group`}
    >
      {/* LED dot — top-right corner */}
      <div
        className={`tpl-led${isActive ? ' tpl-led-active' : ''}`}
        style={isActive ? {
          background: accent,
          boxShadow: `inset 0 1px 1px rgba(255,255,255,0.3), 0 0 4px ${accentGlow}`,
        } : undefined}
      />

      {/* Unpin (×) badge — Pinned zone, top-left corner cutout, hover reveal */}
      {onUnpin && (
        <span
          className="corner-badge corner-badge-tl corner-badge-delete corner-badge-hover"
          onClick={(e) => { e.stopPropagation(); onUnpin(template.id); }}
          role="button"
          tabIndex={-1}
          title={`Unpin ${template.name}`}
          aria-label={`Unpin ${template.name}`}
        >
          <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round">
            <path d="M6 18L18 6M6 6l12 12" />
          </svg>
        </span>
      )}

      {/* Pin badge — Library zone, PINNED state.
          Always visible at 75% (emerald), full green on card hover,
          flashes red-delete on direct badge hover (click will unpin). */}
      {onPin && isPinned && (
        <span
          className="corner-badge corner-badge-tl corner-badge-pin corner-badge-pin-unpinnable corner-badge-persistent group/pin"
          onClick={(e) => { e.stopPropagation(); onPin(template.id); }}
          role="button"
          tabIndex={-1}
          title={`Unpin ${template.name}`}
          aria-label={`Unpin ${template.name}`}
        >
          {/* Pin icon — visible by default, swapped for × on direct hover */}
          <svg className="w-2.5 h-2.5 group-hover/pin:hidden" viewBox="0 0 16 16" fill="currentColor">
            <path d="M4.146.146A.5.5 0 0 1 4.5 0h7a.5.5 0 0 1 .5.5c0 .68-.342 1.174-.646 1.479-.126.125-.25.224-.354.298v4.431l.078.048c.203.127.476.314.751.555C12.36 7.775 13 8.527 13 9.5a.5.5 0 0 1-.5.5h-4v4.5c0 .276-.224 1.5-.5 1.5s-.5-1.224-.5-1.5V10h-4a.5.5 0 0 1-.5-.5c0-.973.64-1.725 1.17-2.189A5.921 5.921 0 0 1 5 6.708V2.277a2.77 2.77 0 0 1-.354-.298C4.342 1.674 4 1.179 4 .5a.5.5 0 0 1 .146-.354z" />
          </svg>
          <svg className="w-2.5 h-2.5 hidden group-hover/pin:block" viewBox="0 0 16 16" fill="currentColor">
            <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
          </svg>
        </span>
      )}

      {/* Pin badge — Library zone, UNPINNED state. Hover reveal to add pin. */}
      {onPin && !isPinned && !pinsFull && (
        <span
          className="corner-badge corner-badge-tl corner-badge-pin corner-badge-hover"
          onClick={(e) => { e.stopPropagation(); onPin(template.id); }}
          role="button"
          tabIndex={-1}
          title={`Pin ${template.name}`}
          aria-label={`Pin ${template.name}`}
        >
          <svg className="w-2.5 h-2.5" viewBox="0 0 16 16" fill="currentColor">
            <path d="M4.146.146A.5.5 0 0 1 4.5 0h7a.5.5 0 0 1 .5.5c0 .68-.342 1.174-.646 1.479-.126.125-.25.224-.354.298v4.431l.078.048c.203.127.476.314.751.555C12.36 7.775 13 8.527 13 9.5a.5.5 0 0 1-.5.5h-4v4.5c0 .276-.224 1.5-.5 1.5s-.5-1.224-.5-1.5V10h-4a.5.5 0 0 1-.5-.5c0-.973.64-1.725 1.17-2.189A5.921 5.921 0 0 1 5 6.708V2.277a2.77 2.77 0 0 1-.354-.298C4.342 1.674 4 1.179 4 .5a.5.5 0 0 1 .146-.354z" />
          </svg>
        </span>
      )}

      {/* SVG thumbnail — 4:5 aspect ratio */}
      <div
        className={`tpl-thumb${isActive ? ' tpl-thumb-active' : ''}`}
        style={isActive ? { color: accent } : undefined}
      >
        {TEMPLATE_THUMBNAILS[template.id] || (
          <div className="w-full h-full rounded" style={{ background: 'rgba(255,255,255,0.03)' }} />
        )}
      </div>

      {/* Template name label */}
      <div
        className={`tpl-label${isActive ? ' tpl-label-active' : ''}`}
        style={isActive ? {
          color: accent,
          textShadow: `0 0 6px ${accentGlow}`,
        } : undefined}
      >
        {template.name}
      </div>
    </button>
  );
}

/**
 * Template Picker — Two-zone instrument-panel catalog.
 *
 * Zone 1 (top): Pinned — user's pinned templates + Import placeholder
 *   - Default: 3 pinned presets + Import
 *   - All pins removable. Import always last.
 *   - Clicking Import expands the Library zone.
 *
 * Zone 2 (bottom): Preset Library — full browsable catalog, collapsible
 *   - Filter pills: This Slide | In Carousel | All | Editorial | Minimal | Bold | ...
 *   - Each card has a pin icon (top-left on hover) to pin/unpin
 *
 * Session 38: Pinning UX with explicit pin/unpin actions.
 */
export function TemplatePicker({ value, onChange, slides = [], defaultTemplate = 'forest-classic' }) {
  const [activeFilter, setActiveFilter] = useState('all');
  const [browseOpen, setBrowseOpen] = useState(false);
  const [pinnedIds, setPinnedIds] = useState(DEFAULT_PINNED_IDS);

  // Set of template IDs currently used in at least one slide
  const usedTemplateIds = useMemo(() => {
    const ids = new Set();
    slides.forEach(s => {
      ids.add(s.template || defaultTemplate);
    });
    return ids;
  }, [slides, defaultTemplate]);

  // Pinned set for O(1) lookup
  const pinnedSet = useMemo(() => new Set(pinnedIds), [pinnedIds]);

  // Unified filter pills: style categories first, then contextual filters at end
  const LIBRARY_FILTERS = useMemo(() => [
    ...TEMPLATE_CATEGORIES,
    { id: 'this-slide', label: 'This Slide' },
    { id: 'in-carousel', label: 'In Carousel' },
  ], []);

  // Filter templates by the active filter
  const filteredTemplates = useMemo(() => {
    if (activeFilter === 'all') return TEMPLATE_LIST;
    if (activeFilter === 'this-slide') return TEMPLATE_LIST.filter(t => t.id === value);
    if (activeFilter === 'in-carousel') return TEMPLATE_LIST.filter(t => usedTemplateIds.has(t.id));
    return TEMPLATE_LIST.filter(t => t.categories?.includes(activeFilter));
  }, [activeFilter, value, usedTemplateIds]);

  // Max pinned items
  const MAX_PINS = 4;
  const pinsFull = pinnedIds.length >= MAX_PINS;

  // Resolve pinned template objects
  const pinnedTemplates = useMemo(() =>
    pinnedIds.map(id => TEMPLATES[id]).filter(Boolean),
  [pinnedIds]);

  // Toggle pin: if pinned → unpin, if unpinned → pin (respects max)
  const handleTogglePin = useCallback((id) => {
    setPinnedIds(prev => {
      if (prev.includes(id)) return prev.filter(pid => pid !== id);
      if (prev.length >= MAX_PINS) return prev;
      return [...prev, id];
    });
  }, []);

  // Unpin a template (from Pinned zone)
  const handleUnpin = useCallback((id) => {
    setPinnedIds(prev => prev.filter(pid => pid !== id));
  }, []);

  // Handle Import click: expand library & scroll to show it
  const handleImport = useCallback(() => {
    setBrowseOpen((prev) => {
      const willOpen = !prev;
      // If we're opening (not closing), scroll to Template section to reveal expansion
      if (willOpen) {
        setTimeout(() => {
          scrollEditorToSection('layout');
        }, 50);
      }
      return willOpen;
    });
  }, []);

  return (
    <div className="space-y-4">
      {/* ═══════ Zone 1: Pinned ═══════ */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="tpl-zone-header">Pinned</div>
        </div>
        <div className="grid grid-cols-4 gap-1.5">
          {pinnedTemplates.map((t) => (
            <TemplateCard
              key={t.id}
              template={t}
              isActive={value === t.id}
              onClick={() => onChange(t.id)}
              onUnpin={handleUnpin}
            />
          ))}

          {/* Import placeholder — visible only when pins < max */}
          {!pinsFull && (
            <div
              className="tpl-import group cursor-pointer"
              title="Browse & pin more templates"
              onClick={handleImport}
              role="button"
              tabIndex={0}
            >
              <div
                className="w-full aspect-[4/5] flex flex-col items-center justify-center transition-colors"
                style={{ color: 'rgba(255,255,255,0.15)' }}
              >
                <svg className="w-4 h-4 mb-0.5 group-hover:text-white/30 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                </svg>
              </div>
              <div
                style={{ fontSize: '9px', color: 'rgba(255,255,255,0.15)', textAlign: 'center', marginTop: '4px' }}
                className="group-hover:text-white/30 transition-colors"
              >
                Import
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ═══════ Zone 2: Preset Library (collapsible) ═══════ */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="tpl-zone-header">Preset Library</div>
        </div>

        {/* Chevron + unified filter pills (contextual first, then categories) */}
        <div className="flex gap-1 overflow-x-auto scrollbar-none pb-2 -mx-1 px-1">
          <button
            onClick={() => setBrowseOpen(v => !v)}
            className={`tpl-chevron${browseOpen ? ' tpl-chevron-open' : ''}`}
            title={browseOpen ? 'Collapse' : 'Expand'}
          >
            <svg
              className="w-3 h-3 transition-transform"
              style={{ transform: browseOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}
              viewBox="0 0 16 16"
              fill="currentColor"
            >
              <path d="M6.22 4.22a.75.75 0 0 1 1.06 0l3.25 3.25a.75.75 0 0 1 0 1.06l-3.25 3.25a.75.75 0 0 1-1.06-1.06L8.94 8 6.22 5.28a.75.75 0 0 1 0-1.06Z" />
            </svg>
          </button>
          {LIBRARY_FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => {
                setActiveFilter(f.id);
                if (!browseOpen) setBrowseOpen(true);
              }}
              className={`tpl-cat-pill${activeFilter === f.id ? ' tpl-cat-pill-active' : ''}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Filtered template grid — visible only when expanded */}
        {browseOpen && (
          <>
            <div className="grid grid-cols-4 gap-1.5 mt-1.5">
              {filteredTemplates.map((t) => (
                <TemplateCard
                  key={t.id}
                  template={t}
                  isActive={value === t.id}
                  onClick={() => onChange(t.id)}
                  onPin={handleTogglePin}
                  isPinned={pinnedSet.has(t.id)}
                  pinsFull={pinsFull}
                />
              ))}
            </div>
            {filteredTemplates.length === 0 && (
              <div className="text-[10px] text-white/20 text-center py-4">
                No templates match this filter
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
